import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, Plane } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  // Check if we should redirect to mobile app after login
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get('redirect') || localStorage.getItem('admin_redirect') || '/admin/dashboard';
  
  // Check if this is a PWA/standalone mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!credentials.email || !credentials.password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      
      login(response.data.access_token, response.data.admin_email, response.data.session_id);
      toast.success('Login successful!');
      
      // Clear any stored redirect
      localStorage.removeItem('admin_redirect');
      
      // Determine destination
      let destination = '/admin/dashboard';
      
      // If launched as PWA/standalone, always go to mobile app
      if (isStandalone) {
        destination = '/admin/app';
      } else if (redirectTo && redirectTo !== '/admin/dashboard') {
        destination = redirectTo;
      }
      
      // Use replace to update URL properly and prevent back button issues
      navigate(destination, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#1A4D7F] to-[#009FB7] flex items-center justify-center px-6"
      data-testid="admin-login-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_5769230e-7a66-4b7d-9294-6503e125a0b4/artifacts/muqtjev5_IMG_5362.jpeg" 
            alt="A-One Tourism Logo" 
            className="w-16 h-16 rounded-full object-cover mx-auto mb-4 shadow-lg border-2 border-white"
          />
          <h1 className="text-3xl font-heading font-bold text-white mb-2">
            Admin Portal
          </h1>
          <p className="text-blue-100">A-One Tourism Visa Management</p>
        </div>

        <Card className="border-0 shadow-2xl">
          <CardHeader>
            <CardTitle className="text-2xl font-heading text-center">Admin Login</CardTitle>
            <CardDescription className="text-center">Enter your credentials to access the dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={credentials.email}
                    onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                    className="pl-10 h-12"
                    data-testid="admin-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={credentials.password}
                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    className="pl-10 h-12"
                    data-testid="admin-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#1A4D7F] hover:bg-[#153e66] h-12 text-lg font-semibold mt-6"
                data-testid="admin-login-button"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/')}
                className="text-sm text-slate-600 hover:text-[#1A4D7F] transition-colors"
                data-testid="back-to-home-link"
              >
                ← Back to Home
              </button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AdminLogin;